/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import usePreciseTimer from '@hooks/usePreciseTimer';
import { noOp } from '@constants/empty';
import H2 from '@base/H2/H2';

const StyledCountdown = styled.div`
  display: flex;
`;

const TimerText = styled(H2)`
  line-height: 20px;
  color: ${({ color }) => color};
`;

const zeroPad = (number) => {
  return `0${number}`.slice(-2);
};

const DHMSTimer = ({
  remainingSeconds,
  onExpire,
  color,
  threshold,
  onThresholdReached,
  onTimerStarted,
  style,
  textStyle
}) => {
  const [expired, setExpired] = useState(false);
  const [isTimerStarted, setIsTimerStarted] = useState(false);
  const [expiryTime, setExpiryTime] = useState(remainingSeconds);
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [timerColor, setTimerColor] = useState(color);
  const reset = () => {
    setSeconds(0);
    setMinutes(0);
    setHours(0);
    setDays(0);
  };

  useEffect(() => {
    reset();
    setExpiryTime(remainingSeconds);
  }, [remainingSeconds]);

  const calculateExpiryDate = useCallback(
    (innerTimeDelay) => {
      if (threshold > expiryTime) {
        onThresholdReached();
        setTimerColor('var(--color-i-red)');
      }

      const distance = expiryTime - innerTimeDelay;
      const tempDays = Math.floor(distance / (3600 * 24));
      const tempHours = Math.floor((distance % (3600 * 24)) / 3600);
      const tempMinutes = Math.floor((distance % 3600) / 60);
      const tempSeconds = Math.floor((distance % 3600) % 60);

      if (distance < 0) {
        reset();
        onExpire();
        setExpired(true);
      } else {
        setSeconds(tempSeconds);
        setMinutes(tempMinutes);
        setHours(tempHours);
        setDays(tempDays);
        setExpiryTime(distance);
      }
      setIsTimerStarted(true);
      onTimerStarted();
    },
    [expiryTime, onExpire, threshold]
  );

  usePreciseTimer(calculateExpiryDate, 1000, !expired);

  return (
    <div style={style}>
      {isTimerStarted && (
        <StyledCountdown>
          {days > 0 ? (
            <>
              <TimerText color={timerColor} style={textStyle}>
                {`${days}d`}
                &nbsp;:&nbsp;
              </TimerText>
              <TimerText color={timerColor} style={textStyle}>
                {`${zeroPad(hours)}h`}
                &nbsp;:&nbsp;
              </TimerText>
              <TimerText color={timerColor} style={textStyle}>
                {zeroPad(minutes)}m
              </TimerText>
            </>
          ) : (
            <>
              {hours > 0 && (
                <TimerText color={timerColor} style={textStyle}>
                  {`${zeroPad(hours)}h`}
                  &nbsp;:&nbsp;
                </TimerText>
              )}
              <TimerText color={timerColor} style={textStyle}>
                {`${zeroPad(minutes)}m`}
                &nbsp;:&nbsp;
              </TimerText>
              <TimerText color={timerColor} style={textStyle}>
                {`${zeroPad(seconds)}s`}
              </TimerText>
            </>
          )}
        </StyledCountdown>
      )}
    </div>
  );
};

export default DHMSTimer;

DHMSTimer.propTypes = {
  remainingSeconds: PropTypes.number, // in seconds
  color: PropTypes.string,
  onExpire: PropTypes.func,
  threshold: PropTypes.number, // in seconds
  onThresholdReached: PropTypes.func,
  onTimerStarted: PropTypes.func,
  style: PropTypes.object,
  textStyle: PropTypes.object
};

DHMSTimer.defaultProps = {
  remainingSeconds: 0,
  color: 'var(--color-text-primary)',
  onExpire: noOp,
  threshold: 0,
  onThresholdReached: noOp,
  onTimerStarted: noOp,
  style: {},
  textStyle: {}
};
